import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Checkbox, FormControlLabel, TextField, Typography, MenuItem } from "@mui/material";
import { approveEdgeOta, bulkApproveOta, getEdgeOta, setEdgeOtaCritical } from "../../api/otaTenantApi";
import { defaultWindow, errDetail, groupByRelease, lookupErrorReason, reasonTr, splitSelection, releaseKind, runPool, toUtcIso, truncate, validateWindow } from "./otaHelpers";

const paper = { sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } };
const muted = { color: "var(--text-muted)" };
const inputSx = { "& .MuiInputBase-root": { color: "var(--text-primary)" }, "& .MuiInputLabel-root": { color: "var(--text-muted)" } };

// edges: [{id, name}]. onClose(changed) — changed=true when the caller should refresh.
export default function OtaApprovalDialog({ open, edges, onClose }) {
  const { t } = useTranslation();
  const token = useSelector((s) => s.session?.accessToken);
  const [meta, setMeta] = useState(null); // id -> {ota|null}
  const [releaseId, setReleaseId] = useState("");
  const [win, setWin] = useState(defaultWindow());
  const [critical, setCritical] = useState(null); // null = leave as is
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const key = edges.map((e) => e.id).join(",");
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setMeta(null); setResults(null); setCritical(null); setWin(defaultWindow());
    runPool(edges, 5, (e) => getEdgeOta(token, e.id).then((ota) => ({ id: e.id, ota }), (err) => ({ id: e.id, ota: null, error: lookupErrorReason(err) }))).then((all) => {
      if (!alive) return;
      const m = Object.fromEntries(all.map((x) => [x.id, x.ota || { error: x.error }]));
      setMeta(m);
      setReleaseId(groupByRelease(all.filter((x) => !x.error).map((x) => ({ edgeId: x.id, release: x.ota?.release }))).groups[0]?.release.id || "");
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key, token]);

  const name = (id) => { const e = edges.find((x) => x.id === id); return e?.name || id; };
  const entries = useMemo(
    () => (meta ? edges.map((e) => ({ edgeId: e.id, release: meta[e.id]?.release, error: meta[e.id]?.error })) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meta, key]
  );
  const groups = useMemo(() => groupByRelease(entries.filter((x) => !x.error)).groups, [entries]);
  const { skipped } = useMemo(() => splitSelection(entries, releaseId), [entries, releaseId]);
  const [showSkipped, setShowSkipped] = useState(false);
  const group = groups.find((g) => g.release.id === releaseId);
  const rel = group?.release;
  const critOn = meta ? edges.filter((e) => meta[e.id]?.require_local_approval).length : 0;
  const critState = critical ?? (critOn === edges.length ? true : critOn === 0 ? false : "mixed");
  const winErr = validateWindow(win.start, win.end);
  const canSubmit = !busy && !!group && !winErr;
  const ids = entries.filter((x) => !x.error && x.release?.id === releaseId).map((x) => x.edgeId);
  const n = ids.length;

  const submit = async () => {
    setBusy(true);
    const body = { release_id: releaseId, window_start: toUtcIso(win.start), window_end: toUtcIso(win.end) };
    try {
      let res;
      if (ids.length === 1) {
        await approveEdgeOta(token, ids[0], body);
        res = [{ edge_id: ids[0], status: "approved", reason: null }];
      } else {
        res = await bulkApproveOta(token, { edge_ids: ids, ...body });
      }
      const extra = skipped.map((x) => ({ edge_id: x.edgeId, status: "skipped", text: x.reason }));
      let critFail = 0;
      if (critical !== null) {
        const todo = edges.filter((e) => !!meta[e.id]?.require_local_approval !== critical);
        const r = await runPool(todo, 5, (e) => setEdgeOtaCritical(token, e.id, critical).then(() => true, () => false));
        critFail = r.filter((x) => !x).length;
      }
      if (critFail || skipped.length) toast.warning(t("edges.ota.approval.toastPartial", { skipped: skipped.length, crit: critFail }));
      else toast.success(t("edges.ota.approval.toastOk", { n: ids.length }));
      setResults({ rows: [...res, ...extra], critFail });
    } catch (err) {
      const s = err?.response?.status;
      toast.error(errDetail(err, t("edges.ota.approval.sendFailed")) + (s ? ` (${s})` : ""));
    } finally {
      setBusy(false);
    }
  };

  const done = !!results;
  const close = () => onClose(done);

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="sm" fullWidth PaperProps={paper}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{done ? t("edges.ota.approval.titleResult") : t("edges.ota.approval.title")}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {!meta && <Typography sx={muted}>{t("edges.loading")}</Typography>}
        {meta && done && (
          <>
            {results.critFail > 0 && <Alert severity="warning" sx={{ mb: 1 }}>{t("edges.ota.approval.critFail", { count: results.critFail })}</Alert>}
            {results.rows.map((r) => (
              <div key={r.edge_id} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border-subtle)", fontSize: 14 }}>
                <b style={{ flex: 1 }}>{name(r.edge_id)}</b>
                {r.status === "approved" ? (
                  <span style={{ color: "var(--success)" }}>{t("edges.ota.approval.approved")}</span>
                ) : (
                  <span style={muted}>{t("edges.ota.approval.skippedLine", { text: r.text || reasonTr(r.reason) })}</span>
                )}
              </div>
            ))}
          </>
        )}
        {meta && !done && (
          <>
            {groups.length === 0 && <Alert severity="info">{t("edges.ota.approval.noUpdates")}</Alert>}
            {groups.length > 1 && (
              <TextField select fullWidth size="small" label={t("edges.ota.approval.release")} value={releaseId} onChange={(e) => setReleaseId(e.target.value)} sx={{ ...inputSx, mb: 2 }}>
                {groups.map((g) => <MenuItem key={g.release.id} value={g.release.id}>{t("edges.ota.approval.releaseOption", { version: g.release.version, n: g.edgeIds.length })}</MenuItem>)}
              </TextField>
            )}
            {rel && (
              <div style={{ padding: 12, background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{rel.version} <span style={{ ...muted, fontSize: 13, fontWeight: 400 }}>{t("edges.ota.approval.edgeCount", { n })}</span></div>
                <div style={{ ...muted, fontSize: 13 }}>
                  {t("edges.ota.approval.channelLine", { kind: releaseKind(rel), channels: [...new Set(ids.map((id) => meta[id]?.channel || "stable"))].join(", ") })}
                </div>
                {rel.release_notes && <div style={{ fontSize: 13, marginTop: 6, whiteSpace: "pre-wrap" }}>{truncate(rel.release_notes)}</div>}
              </div>
            )}
            {skipped.length > 0 && (
              <Alert severity={groups.length > 0 ? "warning" : "info"} sx={{ mb: 2 }}>
                {t("edges.ota.approval.summary", { approve: n, skip: skipped.length })}{" "}
                <a href="#skipped" onClick={(ev) => { ev.preventDefault(); setShowSkipped(!showSkipped); }} style={{ color: "inherit" }}>{showSkipped ? t("edges.ota.approval.hide") : t("edges.ota.approval.show")}</a>
                {showSkipped && skipped.map((x) => <div key={x.edgeId} style={{ fontSize: 13 }}>{name(x.edgeId)}: {x.reason}</div>)}
              </Alert>
            )}
            {rel && (
              <>
                <div style={{ display: "flex", gap: 12, marginBottom: 4 }}>
                  <TextField type="datetime-local" size="small" fullWidth label={t("edges.ota.approval.windowStart")} InputLabelProps={{ shrink: true }} value={win.start} onChange={(e) => setWin({ ...win, start: e.target.value })} sx={inputSx} />
                  <TextField type="datetime-local" size="small" fullWidth label={t("edges.ota.approval.windowEnd")} InputLabelProps={{ shrink: true }} value={win.end} onChange={(e) => setWin({ ...win, end: e.target.value })} sx={inputSx} />
                </div>
                {winErr && <Typography variant="caption" sx={{ color: "var(--danger)" }}>{winErr}</Typography>}
                <div style={{ marginTop: 8 }}>
                  <FormControlLabel
                    control={<Checkbox checked={critState === true} indeterminate={critState === "mixed"} onChange={() => setCritical(critState === true ? false : true)} />}
                    label={t("edges.ota.approval.criticalLabel")}
                  />
                  <Typography variant="caption" sx={{ ...muted, display: "block", ml: 4, mt: -0.5 }}>
                    {t("edges.ota.approval.criticalHelp")}{critState === "mixed" ? t("edges.ota.approval.mixed", { on: critOn, total: edges.length }) : ""}
                  </Typography>
                </div>
                <Typography variant="caption" sx={{ ...muted, display: "block", mt: 2 }}>
                  {t("edges.ota.approval.note")}
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        {done ? (
          <Button onClick={close} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("common.close")}</Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy} sx={muted}>{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={!canSubmit} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("edges.ota.approval.submit", { n })}</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
